import { ChangeDetectionStrategy, Component, EventEmitter, Output } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BaseComponent, HelperService, UserService } from "@mypxplat/xplat/core";

/**
 * Email-link recovery for a Fender-only account whose PreSonus link paeapi can't prove (401
 * FENDER_EMAIL_VERIFY_REQUIRED). These accounts have no PreSonus login to re-link with, so the user
 * proves the mailbox instead: paeapi emails a one-click link to the account's address, and opening it
 * (web-fender /verify-account, any device) binds this Fender account. "I've confirmed" emits
 * `verified` so the caller can reload; closing without it is the caller's cue to sign out.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-fender-email-verify",
  templateUrl: "fender-email-verify.component.html",
  standalone: false,
})
export class FenderEmailVerifyComponent extends BaseComponent {
  @Output() verified: EventEmitter<any> = new EventEmitter();

  /**
   * "fender": finish linking a Fender-only account (FENDER_EMAIL_VERIFY_REQUIRED); the send endpoint
   * authorizes from `accessToken`. "employee": verify a @fender.com / @presonus.com mailbox before any
   * employee benefit; blocking — no Close, only verify or log out (closes with "logout").
   */
  public mode: "fender" | "employee" = "fender";
  /** The Fender access token of the account being verified (fender mode only). */
  public accessToken: string = "";
  /** Display only: the account's email, shown before a link is sent. */
  public email: string = "";

  public destination: string | null = null;
  public linkSent: boolean = false;
  public loading: boolean = false;
  public errorMessage: string = "";

  constructor(
    public userService: UserService,
    public helperService: HelperService,
    public activeModal: NgbActiveModal
  ) {
    super(userService);
  }

  sendLink() {
    if (this.loading || (this.mode === "fender" && !this.accessToken)) return;
    this.loading = true;
    this.errorMessage = "";
    const send$ = this.mode === "employee" ? this.userService.sendEmployeeVerification() : this.userService.sendFenderEmailVerification(this.accessToken);
    send$.subscribe({
      next: (result: any) => {
        this.loading = false;
        if (result?.alreadyVerified) return this.confirmed();
        this.linkSent = true;
        this.destination = result?.destination || null;
      },
      error: (error) => {
        this.loading = false;
        this.errorMessage = this.helperService.retrieveErrorMessage(error);
      },
    });
  }

  logOut() {
    this.activeModal.close("logout");
  }

  /** The user opened the link (here or on another device): let the caller reload and retry. */
  confirmed() {
    this.verified.emit(true);
    this.activeModal.close("verified");
  }
}
